import { Fragment, memo, useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Typography, useEventCallback } from '@mui/material';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { useNavigate } from 'react-router-dom';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import {
  HttpRequestFunction,
  RequestOptionsOverride,
  ResponsePromise,
} from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpBackendError } from 'shell/error/definitions/http-backend-error';
import { HttpResponseList } from 'shell/models/http-response';
import { transformDataset } from 'practice/pages/responsible/utils';
import { ResponsibleData } from 'shell/models/type-responsible';
import {
  BodyResearchResponsible,
  ResearchResponsibleVM,
  ResearchResponsibleTableVM,
} from 'practice/view-models/research-responsible';

interface TableResponsibleProps {
  activeDelete?: boolean;
  activeEdit?: boolean;
  setDeleteAlertModal: (value: AlertModal<BodyResearchResponsible>) => void;
  getResponsible:
    | ((options?: RequestOptionsOverride | undefined) => Promise<Error | HttpBackendError>)
    | HttpRequestFunction<HttpResponseList<ResponsibleData>, BodyResearchResponsible[]>;
  responsibles: Array<BodyResearchResponsible>;
}

const TableResponsibleComponent = ({
  activeDelete,
  activeEdit,
  getResponsible,
  setDeleteAlertModal,
  responsibles,
}: TableResponsibleProps): React.ReactElement => {
  const formContext = useFormikContext<ResearchResponsibleVM>();

  const navigate = useNavigate();

  const onChange = useEventCallback(
    (
      key: keyof ResearchResponsibleTableVM,
      value: ResearchResponsibleTableVM[keyof ResearchResponsibleTableVM]
    ) => {
      formContext.setFieldValue('table', {
        ...(formContext.values.table || {}),
        [key]: value,
      });
    }
  );

  const onChanges = useEventCallback((values: ResearchResponsibleTableVM) => {
    formContext.setFieldValue('table', {
      ...(formContext.values.table || {}),
      ...values,
    });
  });

  const onChangePage = useEventCallback((page: number) => {
    onChange('page', page || 0);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChanges({
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyResearchResponsible): Promise<BodyResearchResponsible> => {
      return new Promise(() => {
        navigate(`/pratiche/ricerca-responsabile/dettaglio/${cell?.practice}/${cell?.service}`);
      });
    }
  );

  const onEditPress = useEventCallback(
    (cell: BodyResearchResponsible): Promise<BodyResearchResponsible> => {
      return new Promise(() => {
        navigate(`/pratiche/ricerca-responsabile/modifica/${cell?.practice}/${cell?.service}`);
      });
    }
  );

  const onTrashPress = useCallback(
    (cell: BodyResearchResponsible): Promise<BodyResearchResponsible> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<BodyResearchResponsible>;
    },
    [setDeleteAlertModal]
  );

  useEffect(() => {
    if (responsibles?.length > 0) {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: responsibles,
      });
    } else {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: responsibles,
      });
    }
  }, [responsibles]);

  const onSort = useCallback(
    (orders) => {
      const newDataset = {
        ...formContext.values,
        filter: {
          ...formContext.values?.filter,
          orders,
        },
      };
      const queryString = transformDataset(newDataset);
      (
        getResponsible({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<ResponsibleData>, Array<BodyResearchResponsible>>
      ).then(() => {
        formContext.setFieldValue('filter', {
          ...{
            ...formContext.values?.filter,
            orders,
          },
        });
      });
    },
    [formContext.setFieldValue, getResponsible, formContext.values]
  );
  return (
    <Fragment>
      {formContext.values.table?.body ? (
        (formContext.values.table?.body || [])?.length > 0 ? (
          <Table
            ariaLabel="Lista responsabili"
            page={formContext.values.table?.page || 0}
            rowsPerPage={formContext.values.table?.rowsPerPage}
            body={formContext.values.table?.body || []}
            count={(formContext.values.table?.body || [])?.length}
            head={headCells}
            onSort={onSort}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            orders={formContext.values.filter?.orders}
            isNotLayoutFixed
            activeActions={{
              delete: activeDelete,
              read: true,
              edit: activeEdit,
            }}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

TableResponsibleComponent.displayName = 'Table responsible component';

const TableResponsible = memo(TableResponsibleComponent);

export default TableResponsible;
